<template>
  <div class="email-template-view-page flex-1 overflow-auto">
    <woot-loading-state
      v-if="uiFlags.isFetching"
      :message="$t('EMAIL_TEMPLATES.EDITOR.LOADING')"
    />
    <div v-else-if="template" class="email-template-view">
      <div class="viewer-header">
        <div class="viewer-header-main">
          <woot-button
            variant="clear"
            size="small"
            icon="arrow-chevron-left"
            color-scheme="secondary"
            class="email-template-back-btn"
            @click="goBackToList"
          >
            {{ $t('EMAIL_TEMPLATES.BACK_TO_LIST') }}
          </woot-button>
          <h2 class="header-title">
            {{ template.name }}
          </h2>
        </div>
      </div>

      <div class="content-wrapper">
        <div class="draft-info-cards">
          <div class="info-card">
            <div class="card-label">{{ $t('EMAIL_TEMPLATES.EDITOR.VISIBILITY_LABEL') }}</div>
            <div class="card-value">
              <span
                class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                :class="
                  template.visibility === 'global'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
                "
              >
                {{
                  template.visibility === 'global'
                    ? $t('EMAIL_TEMPLATES.VISIBILITY.PUBLIC')
                    : $t('EMAIL_TEMPLATES.VISIBILITY.PRIVATE')
                }}
              </span>
            </div>
          </div>
          <div class="info-card">
            <div class="card-label">{{ $t('EMAIL_TEMPLATES.VIEW.CREATED_AT') }}</div>
            <div class="card-value">{{ formatDateTime(template.created_at) }}</div>
          </div>
          <div class="info-card">
            <div class="card-label">{{ $t('EMAIL_TEMPLATES.VIEW.UPDATED_AT') }}</div>
            <div class="card-value">{{ formatDateTime(template.updated_at) }}</div>
          </div>
        </div>

        <div class="preview-header">
          <h3 class="preview-title">{{ $t('EMAIL_TEMPLATES.VIEW.PREVIEW_SECTION') }}</h3>
          <div class="preview-actions">
            <woot-button
              variant="smooth"
              color-scheme="secondary"
              icon="edit"
              @click="goToEdit"
            >
              {{ $t('EMAIL_TEMPLATES.EDIT_BTN') }}
            </woot-button>
            <woot-button
              variant="smooth"
              color-scheme="secondary"
              icon="eye-show"
              @click="openPreview"
            >
              {{ $t('EMAIL_TEMPLATES.PREVIEW_BTN') }}
            </woot-button>
            <woot-button
              variant="smooth"
              color-scheme="alert"
              icon="delete"
              @click="openDeleteModal"
            >
              {{ $t('EMAIL_TEMPLATES.DELETE_BTN') }}
            </woot-button>
          </div>
        </div>

        <div class="subject-section">
          <label class="section-label">{{ $t('EMAIL_TEMPLATES.EDITOR.SUBJECT_LABEL') }}</label>
          <p class="subject-text">{{ template.subject }}</p>
        </div>

        <div class="content-section">
          <label class="section-label">{{ $t('EMAIL_TEMPLATES.EDITOR.BODY_LABEL') }}</label>
          <div
            class="content-display prose dark:prose-invert max-w-none text-sm"
            v-dompurify-html="formattedBody"
          />
        </div>

        <div class="attachments-section">
          <label class="section-label">{{ $t('EMAIL_TEMPLATES.EDITOR.ATTACHMENTS_LABEL') }}</label>
          <div v-if="template.attachments && template.attachments.length" class="attachments-list">
            <div
              v-for="att in template.attachments"
              :key="att.id"
              class="attachment-item-preview"
            >
              <div class="attachment-thumbnail">
                <fluent-icon icon="document" size="22" class="file-icon text-slate-500" />
              </div>
              <div class="attachment-details">
                <a
                  v-if="att.url"
                  :href="att.url"
                  target="_blank"
                  rel="noopener"
                  class="attachment-name text-woot-500 hover:underline"
                >
                  {{ att.filename }}
                </a>
                <span v-else class="attachment-name">{{ att.filename }}</span>
              </div>
            </div>
          </div>
          <p v-else class="no-attachments">{{ $t('EMAIL_TEMPLATES.VIEW.NO_ATTACHMENTS') }}</p>
        </div>
      </div>
    </div>
    <p v-else-if="!uiFlags.isFetching" class="p-4 text-slate-600 dark:text-slate-400">
      {{ $t('EMAIL_TEMPLATES.ERROR_LOAD') }}
    </p>

    <woot-delete-modal
      :show.sync="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDelete"
      :title="$t('EMAIL_TEMPLATES.DELETE.CONFIRM.TITLE')"
      :message="$t('EMAIL_TEMPLATES.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('EMAIL_TEMPLATES.DELETE.CONFIRM.YES')"
      :reject-text="$t('EMAIL_TEMPLATES.DELETE.CONFIRM.NO')"
    />
    <woot-modal
      content-class="email-template-preview-modal-container"
      :show.sync="showPreviewModal"
      :on-close="closePreview"
    >
      <email-template-preview-content
        v-if="previewData"
        :subject="previewData.subject"
        :body-html="formattedPreviewBody"
        :attachments="previewData.attachments || []"
      />
    </woot-modal>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import accountMixin from 'dashboard/mixins/account.js';
import EmailTemplatesAPI from 'dashboard/api/emailTemplates';
import MessageFormatter from 'shared/helpers/MessageFormatter';

import EmailTemplatePreviewContent from './EmailTemplatePreviewContent.vue';

export default {
  components: { EmailTemplatePreviewContent },
  mixins: [alertMixin, accountMixin],
  data() {
    return {
      template: null,
      uiFlags: { isFetching: true },
      showDeleteModal: false,
      showPreviewModal: false,
      previewData: null,
    };
  },
  computed: {
    templateId() {
      return this.$route.params.templateId;
    },
    formattedBody() {
      if (!this.template?.body) return '';
      return new MessageFormatter(this.template.body).formattedMessage;
    },
    formattedPreviewBody() {
      if (!this.previewData?.body) return '';
      return new MessageFormatter(this.previewData.body).formattedMessage;
    },
  },
  watch: {
    templateId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchTemplate();
      },
    },
  },
  mounted() {
    this.fetchTemplate();
  },
  methods: {
    goBackToList() {
      this.$router.push(this.addAccountScoping('settings/email_templates'));
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return '—';
      try {
        return d.toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        });
      } catch {
        return d.toISOString();
      }
    },
    async fetchTemplate() {
      if (!this.templateId) return;
      this.uiFlags.isFetching = true;
      try {
        const { data } = await EmailTemplatesAPI.show(this.templateId);
        this.template = data;
      } catch (e) {
        this.template = null;
        this.showAlert(this.$t('EMAIL_TEMPLATES.ERROR_LOAD'));
      } finally {
        this.uiFlags.isFetching = false;
      }
    },
    goToEdit() {
      this.$router.push(this.addAccountScoping(`settings/email_templates/${this.templateId}/edit`));
    },
    async openPreview() {
      try {
        const { data } = await EmailTemplatesAPI.getPreview(this.templateId);
        this.previewData = data;
        this.showPreviewModal = true;
      } catch (e) {
        this.showAlert(this.$t('EMAIL_TEMPLATES.PREVIEW_ERROR'));
      }
    },
    closePreview() {
      this.showPreviewModal = false;
      this.previewData = null;
    },
    openDeleteModal() {
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    async confirmDelete() {
      try {
        await EmailTemplatesAPI.delete(this.templateId);
        this.showAlert(this.$t('EMAIL_TEMPLATES.DELETE.SUCCESS'));
        this.$router.push(this.addAccountScoping('settings/email_templates'));
      } catch (e) {
        this.showAlert(this.$t('EMAIL_TEMPLATES.DELETE.ERROR'));
      }
      this.closeDeleteModal();
    },
  },
};
</script>

<style lang="scss" scoped>
.email-template-view-page {
  background-color: var(--color-background-light);
  width: 100%;
}

body.dark .email-template-view-page {
  background-color: #151718;
}

.email-template-view {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.viewer-header {
  background-color: var(--white);
  border-bottom: 1px solid var(--color-border);
  padding: var(--space-normal) var(--space-large);
  position: relative;
}

body.dark .viewer-header {
  background-color: #151718;
  border-bottom-color: var(--color-border);
}

.viewer-header-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-small);

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-normal);
  }
}

.header-title {
  margin: 0;
  font-size: var(--font-size-large);
  font-weight: var(--font-weight-bold);
  color: var(--color-heading);
}

body.dark .header-title {
  color: #f3f4f6;
}

.email-template-back-btn {
  background-color: #40518a !important;
  color: white !important;
  padding: 10px 14px !important;
  line-height: 1.2 !important;
  border-radius: var(--border-radius-medium) !important;
  border: 1px solid #334074 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.content-wrapper {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: var(--space-normal) var(--space-large) var(--space-large);
}

.draft-info-cards {
  display: flex;
  gap: var(--space-normal);
  margin-bottom: var(--space-normal);
  flex-wrap: wrap;
}

.info-card {
  flex: 1;
  min-width: 180px;
  background-color: var(--white);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  padding: var(--space-normal);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .card-label {
    font-size: var(--font-size-mini);
    font-weight: var(--font-weight-medium);
    color: var(--color-body);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: var(--space-small);
  }

  .card-value {
    font-size: var(--font-size-default);
    font-weight: var(--font-weight-medium);
    color: var(--color-heading);
    word-break: break-word;
  }
}

body.dark .info-card {
  background-color: #1a1d1f;
  border-color: var(--color-border);
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-small);
  padding: var(--space-normal) 0;

  .preview-title {
    margin: 0;
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    color: var(--color-heading);
  }

  .preview-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-small);
    align-items: center;
  }
}

body.dark .preview-header .preview-title {
  color: #f3f4f6;
}

.section-label {
  display: block;
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-bold);
  color: var(--color-heading);
  margin-bottom: 0;
}

body.dark .section-label {
  color: #e5e7eb;
}

.subject-section {
  margin-bottom: var(--space-normal);
  background-color: var(--white);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  padding: var(--space-normal);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.subject-text {
  padding: var(--space-small) var(--space-normal);
  background-color: var(--color-background);
  border-radius: var(--border-radius-small);
  margin-top: var(--space-small);
  margin-bottom: 0;
  color: var(--color-heading);
  word-break: break-word;
}

body.dark .subject-section {
  background-color: #1a1d1f;
}

body.dark .subject-text {
  background-color: #252a2e;
  color: #f3f4f6;
}

.content-section {
  margin-bottom: var(--space-normal);
  background-color: var(--white);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  padding: var(--space-normal);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.content-display {
  padding: var(--space-normal);
  background-color: var(--color-background);
  border-radius: var(--border-radius-small);
  min-height: 120px;
  overflow: auto;
  word-wrap: break-word;
  margin-top: var(--space-small);

  :deep(a) {
    color: var(--w-500);
    text-decoration: underline;
    word-break: break-all;

    &:hover {
      color: var(--w-600);
    }
  }

  :deep(p) {
    margin: 0 0 var(--space-small) 0;
    white-space: pre-wrap;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(strong) {
    font-weight: var(--font-weight-bold);
  }

  :deep(em) {
    font-style: italic;
  }
}

body.dark .content-section {
  background-color: #1a1d1f;
}

body.dark .content-display {
  background-color: #252a2e;
}

.attachments-section {
  margin-bottom: var(--space-normal);
  background-color: var(--white);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  padding: var(--space-normal);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.attachments-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-small);
  margin-top: var(--space-small);
}

.attachment-item-preview {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-small);
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-small);
  max-width: 100%;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  }
}

.attachment-thumbnail {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--white);
  border-radius: var(--border-radius-small);
}

.attachment-details {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-small);
  color: var(--color-heading);
  word-break: break-all;
}

.no-attachments {
  color: var(--color-body-light);
  font-style: italic;
  margin-top: var(--space-small);
  margin-bottom: 0;
}

body.dark .attachments-section {
  background-color: #1a1d1f;
}

body.dark .attachment-item-preview {
  background-color: #252a2e;
}
</style>
